import { useState } from "react";
import { Bell, Volume2, VolumeX, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useNotifications } from "@/contexts/NotificationContext";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";

export default function NotificationBell() {
  const { items, unread, markRead, markAllRead, soundEnabled, setSound, playSample } = useNotifications();
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button data-testid="notif-bell" size="icon" variant="ghost" className="relative">
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-accent text-accent-foreground text-[10px] font-mono font-bold flex items-center justify-center animate-pulse-ring"
              data-testid="notif-unread-count"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[380px] p-0 glass border-border/60 shadow-2xl"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/60">
          <div>
            <div className="font-display font-bold text-sm">Notifications</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{unread} unread</div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              data-testid="toggle-sound-btn"
              size="icon"
              variant="ghost"
              onClick={() => { setSound(!soundEnabled); if (!soundEnabled) playSample?.(); }}
              aria-label="Toggle sound"
            >
              {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </Button>
            <Button data-testid="mark-all-read-btn" size="icon" variant="ghost" onClick={markAllRead}>
              <CheckCheck className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {items.length === 0 && (
            <div className="p-8 text-center text-sm text-muted-foreground">You're all caught up.</div>
          )}
          {items.map((n) => (
            <Link
              key={n.notification_id}
              to={n.link || "/notifications"}
              onClick={() => { if (!n.read) markRead(n.notification_id); setOpen(false); }}
              className={`flex gap-3 px-4 py-3 border-b border-border/40 hover:bg-secondary/50 transition-colors ${!n.read ? "bg-primary/5" : ""}`}
              data-testid={`notif-item-${n.notification_id}`}
            >
              <div className={`mt-1 h-2 w-2 rounded-full shrink-0 ${!n.read ? "bg-primary" : "bg-transparent"}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{n.title}</div>
                <div className="text-xs text-muted-foreground line-clamp-2">{n.body}</div>
                <div className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                </div>
              </div>
            </Link>
          ))}
        </div>
        <div className="p-2 border-t border-border/60">
          <Link
            to="/notifications"
            onClick={() => setOpen(false)}
            className="block text-center text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-primary py-1.5"
            data-testid="view-all-notifications"
          >
            View all
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
